import posthog from 'posthog-js';

class PostHogService {
  private initialized = false;

  public initForEnv(windowLocation: String): void {
    // Initialize PostHog analytics with public keys
    const postHogHost = "https://us.i.posthog.com";
    let postHogKey = "";
    if (windowLocation.includes('staging.v2.chameleoncreator.com')) {
      // Staging
      postHogKey = "phc_Er5x4m9QLEikLrmTS4oCb8aaN9kTVoAJ38AIh8OLf3D";
    } else if (windowLocation.includes('v2.chameleoncreator.com')) {
      // Production
      postHogKey = "phc_VXmTiKHNdzYJs06DnIC9GBUmYLht5NeOus93FYMNvDD";
    } else {
      // Development
      postHogKey = "phc_9e2LAUifacU3n61dZQcHWGZfgiFpETGNGAzujzvqAGz";
    }
    this.init(postHogKey, postHogHost);
  }

  /**
   * Initialize PostHog - should be called once at app startup
   */
  public init(posthogApiKey: string, posthogHost: string): void {
    if (this.initialized || !posthogApiKey) {
      return;
    }

    try {
      const isLocalHost = window.location.toString().includes('localhost:');
      posthog.init(posthogApiKey, {
        api_host: posthogHost || 'https://app.posthog.com',
        // Environment-specific settings
        debug: isLocalHost,
        autocapture: {
          // Disable autocapture for editor elements to avoid noise
          css_selector_allowlist: [
            '[data-ph-capture="true"]',
          ],
          element_allowlist: ['button', 'a'],
        },
        // Capture pageviews manually to control what gets tracked
        capture_pageview: false,
      });

      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
    }
  }

  /**
   * Identify the current user with PostHog
   */
  public identifyUser(userData: { id: string, accountId: string, accountName: string, projectsCount: number, usersCount: number }): void {
    if (!this.initialized) {
      return;
    }

    try {
      // Identify user
      posthog.identify(userData.id, userData);

    } catch (error) {
      console.error('Failed to identify user with PostHog:', error);
    }
  }

  /**
   * Track custom events
   */
  public track(eventName: string, properties: Record<string, any> = {}): void {
    if (!this.initialized) {
      return;
    }

    try {
      posthog.capture(eventName, properties);
    } catch (error) {
      console.error('Failed to track event:', eventName, error);
    }
  }

  /**
   * Track page views manually
   */
  public trackPageView(pageName?: string): void {
    if (!this.initialized) {
      return;
    }

    try {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        page_name: pageName
      });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  /**
   * Set user properties
   */
  public setUserProperties(properties: Record<string, any>): void {
    if (!this.initialized) {
      return;
    }

    try {
      posthog.people.set(properties);
    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  /**
   * Reset user (for logout scenarios)
   */
  public reset(): void {
    if (!this.initialized) {
      return;
    }

    try {
      posthog.reset();
    } catch (error) {
      console.error('Failed to reset PostHog:', error);
    }
  }

  /**
   * Get the PostHog instance (for advanced usage)
   */
  public getInstance() {
    return posthog;
  }
}

// Create singleton instance following the pattern used elsewhere in the codebase
const postHogService = new PostHogService();

export default postHogService;
export { PostHogService };
